/**
 * Piccard UI Library - FlashCard Component
 *
 * 3D flip card with flat editorial styling
 * Perfect for flashcards, reveal interactions
 */

import type { ReactNode, HTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

export interface FlashCardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Content for the front face (visible initially)
   */
  front: ReactNode

  /**
   * Content for the back face (visible after flip)
   */
  back: ReactNode

  /**
   * Whether the card is flipped (shows back face)
   * @default false
   */
  isFlipped?: boolean

  /**
   * Callback when card is clicked
   */
  onFlip?: () => void
}

/**
 * 3D flip card with flat editorial styling
 *
 * Features:
 * - Flat shadow with hairline border
 * - 3D flip animation (180deg rotation)
 * - Perspective container for depth
 * - Click to flip interaction
 * - Programmatic flip control
 * - Backface visibility hidden for clean flip
 */
export function FlashCard({
  front,
  back,
  isFlipped = false,
  onFlip,
  className = '',
  ...props
}: FlashCardProps) {
  return (
    <div
      className={cn(
        // Perspective container for 3D effect
        'perspective-container',
        'w-full',
        'aspect-[4/5]',
        'max-h-[350px]',
        // Clickable cursor
        onFlip && 'cursor-pointer',
        // Custom classes
        className
      )}
      onClick={onFlip}
      {...props}
    >
      {/* Inner flip wrapper */}
      <div
        className={cn(
          // Base styles
          'flip-card-inner',
          'relative',
          'w-full',
          'h-full',
          'border border-hairline rounded-xl shadow-card',
          // Transition
          'transition-transform',
          'duration-[600ms]',
          // Flip state
          isFlipped && 'is-flipped'
        )}
      >
        {/* Front Face */}
        <div className="card-face absolute inset-0 flex flex-col items-center justify-center p-8 text-center bg-surface-card rounded-xl">
          {front}
        </div>

        {/* Back Face */}
        <div className="card-face card-back absolute inset-0 flex flex-col p-0 bg-surface-card rounded-xl overflow-hidden">
          {back}
        </div>
      </div>
    </div>
  )
}
